"use client";

import { Fragment, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useT, useLocale } from "@/lib/locale-context";
import { useLocalizedRouter } from "@/lib/localized-router";
import SharedHeader from "@/components/SharedHeader";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

// Matches the backend's in-process resend throttle (60s per user,
// see routes/auth.py). The visible countdown keeps the button honest
// instead of letting a second click bounce off a silent 429.
const RESEND_COOLDOWN_S = 60;

/**
 * Blocking gate for signed-in users whose email isn't confirmed yet, on
 * every feature that requires a verified address (map, chat, alerts,
 * history). OAuth signups land verified, so this only stops email/password
 * accounts that haven't opened the confirmation link. It mirrors the
 * resend flow of VerifyEmailBanner but replaces the feature entirely
 * instead of just nudging from the header - the matching backend routes
 * return 403 `email_not_verified`, so this is the UX for that contract.
 *
 * Round 10 anatomy: shows the ACTUAL address inside the sentence, one
 * indigo resend action with a visible cooldown timer, and an
 * "Ünvanı dəyiş" escape (sign out -> register) for wrong-address
 * signups. No envelope illustration.
 *
 * Renders the normal page chrome (SharedHeader) so the nav bar stays put,
 * and uses plain gray utilities only: the app inverts the gray scale in
 * dark mode via CSS vars (globals.css), so adding `dark:` overrides would
 * fight that and make text vanish.
 */
export default function VerifyEmailGate() {
  const t = useT();
  const { locale } = useLocale();
  const { user, signOut } = useAuth();
  const router = useLocalizedRouter();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  // Epoch ms until which resend stays locked; null = no cooldown.
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [nowTs, setNowTs] = useState(() => Date.now());

  // Remember WHERE this gate interrupted the user. An email/password
  // signup arriving from an anonymous draft (alert builder, track-home
  // form with ?prefill=/query params) hits this wall, then opens the
  // verification link - which lands on /auth/verify with no memory of
  // the draft URL. The verify page reads this key and returns here, so
  // "your selections are kept" stays true on the email path too.
  useEffect(() => {
    try {
      localStorage.setItem(
        "menzil.post_verify_return",
        JSON.stringify({
          path: window.location.pathname + window.location.search,
          ts: Date.now(),
        }),
      );
    } catch { /* private mode - the draft URL is simply not restored */ }
  }, []);

  // 1s tick while a cooldown is live so the "0:54" counter moves.
  useEffect(() => {
    if (!cooldownUntil) return;
    const id = setInterval(() => {
      const now = Date.now();
      setNowTs(now);
      // Over: drop the cooldown so the tick stops (it re-rendered the
      // gate every second for as long as it stayed mounted).
      if (now >= cooldownUntil) setCooldownUntil(null);
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const cooldownLeft = cooldownUntil
    ? Math.max(0, Math.ceil((cooldownUntil - nowTs) / 1000))
    : 0;
  const inCooldown = cooldownLeft > 0;

  const formatCooldown = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const handleResend = async () => {
    if (sending || inCooldown) return;
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ locale }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(
          (typeof data.detail === "string" && data.detail) ||
            t("auth.verify.resend.failed"),
        );
      } else {
        setSent(true);
        setCooldownUntil(Date.now() + RESEND_COOLDOWN_S * 1000);
      }
    } catch {
      setError(t("auth.verify.network"));
    } finally {
      setSending(false);
    }
  };

  // Wrong-address escape: there is no change-email endpoint, so the
  // honest way out is a fresh signup with the right address.
  const handleChangeAddress = async () => {
    await signOut();
    router.push("/auth/register");
  };

  // Body copy embeds the real address in bold: "aysel@example.az
  // ünvanına link göndərdik...". Split on the {email} placeholder so
  // each locale keeps its own word order.
  const email = user?.email ?? "";
  const bodyParts = t("auth.verifygate.body").split("{email}");

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader />
      <div className="flex min-h-[70vh] w-full items-center justify-center px-4 py-16">
        <div className="max-w-md w-full rounded-2xl border border-gray-200 bg-white p-6 sm:p-7">
          <h1 className="text-lg font-bold tracking-tight text-gray-900">
            {t("auth.verifygate.title")}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            {bodyParts.map((part, i) => (
              <Fragment key={i}>
                {part}
                {i < bodyParts.length - 1 && (
                  <span className="font-semibold text-gray-900 break-all">
                    {email}
                  </span>
                )}
              </Fragment>
            ))}
          </p>

          <div className="mt-5">
            {inCooldown && sent ? (
              <p className="text-sm font-semibold text-emerald-700">
                {t("auth.verify.resend.sent")}
              </p>
            ) : (
              <button
                onClick={handleResend}
                disabled={sending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("auth.verifygate.resend")}
              </button>
            )}
            {error && <p className="mt-3 text-xs text-rose-700">{error}</p>}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              onClick={handleChangeAddress}
              className="text-xs font-semibold text-gray-500 transition-colors hover:text-gray-700"
            >
              {t("auth.verifygate.change")}
            </button>
            {inCooldown && (
              <span className="text-xs text-gray-400 tabular-nums">
                {t("auth.verifygate.cooldown").replace(
                  "{t}",
                  formatCooldown(cooldownLeft),
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
